import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, map } from "rxjs";
import { Student, StudentWithHolds } from "./student.model";
import { Hold } from "./hold.model";
import { CreateHoldApiRequest } from "./api.model";

@Injectable({
  providedIn: "root",
})
export class StudentService {
  constructor(private http: HttpClient) {}

  // Map API student to your Student model
  private mapToStudent(apiStudent: any): Student {
    console.log("Mapping API student:", apiStudent);

    return {
      id: apiStudent.studentId,
      name: apiStudent.studentName,
      fullName: apiStudent.fullName || apiStudent.studentName,
      email: apiStudent.email,
      studentId: apiStudent.studentId,
      activeHolds: apiStudent.activeHolds,
      totalHolds: apiStudent.totalHolds,
    };
  }

  private mapToHold(apiHold: any): Hold {
    console.log("Mapping API hold:", apiHold);

    // Determine hold type based on category or name
    let type: Hold["type"] = "Administrative";
    const holdName = (apiHold?.customHoldName || "").toLowerCase().trim();

    console.log("Hold name for matching:", holdName);

    // Financial holds
    if (
      holdName.includes("finance") ||
      holdName.includes("financial") ||
      holdName.includes("fee") ||
      holdName.includes("payment") ||
      holdName.includes("tuition") ||
      holdName.includes("balance") ||
      holdName.includes("money")
    ) {
      type = "Finance";
    }
    // Academic holds
    else if (
      holdName.includes("academic") ||
      holdName.includes("grade") ||
      holdName.includes("course") ||
      holdName.includes("study") ||
      holdName.includes("prerequisite")
    ) {
      type = "Academic";
    }
    // Admissions holds
    else if (
      holdName.includes("admission") ||
      holdName.includes("apply") ||
      holdName.includes("application")
    ) {
      type = "Admissions";
    }
    // Enrollment holds
    else if (holdName.includes("enrollment") || holdName.includes("enrol")) {
      type = "Enrollment";
    }
    // Registration holds
    else if (
      holdName.includes("registration") ||
      holdName.includes("register")
    ) {
      type = "Registration";
    }
    // Attendance holds
    else if (
      holdName.includes("attendance") ||
      holdName.includes("participat") ||
      holdName.includes("absent")
    ) {
      type = "Attendance";
    }
    // Clinical holds
    else if (
      holdName.includes("clinical") ||
      holdName.includes("rotation") ||
      holdName.includes("hospital") ||
      holdName.includes("medical") ||
      holdName.includes("compliance")
    ) {
      type = "Clinical";
    }
    // Disciplinary holds
    else if (
      holdName.includes("disciplinary") ||
      holdName.includes("conduct") ||
      holdName.includes("behavior") ||
      holdName.includes("violation")
    ) {
      type = "Disciplinary";
    }
    // Records holds
    else if (
      holdName.includes("records") ||
      holdName.includes("record") ||
      holdName.includes("document") ||
      holdName.includes("transcript") ||
      holdName.includes("file") ||
      holdName.includes("paperwork")
    ) {
      type = "Records";
    }
    // Graduation holds
    else if (
      holdName.includes("graduation") ||
      holdName.includes("graduate") ||
      holdName.includes("degree") ||
      holdName.includes("diploma") ||
      holdName.includes("completion")
    ) {
      type = "Graduation";
    }
    // Library holds
    else if (
      holdName.includes("library") ||
      holdName.includes("book") ||
      holdName.includes("overdue") ||
      holdName.includes("fine") ||
      holdName.includes("return")
    ) {
      type = "Library";
    }
    // Testing holds
    else if (
      holdName.includes("testing") ||
      holdName.includes("test") ||
      holdName.includes("exam") ||
      holdName.includes("credential") ||
      holdName.includes("placement")
    ) {
      type = "Testing";
    }
    // Administrative holds
    else if (
      holdName.includes("administrative") ||
      holdName.includes("admin") ||
      holdName.includes("system") ||
      holdName.includes("processing") ||
      holdName.includes("form")
    ) {
      type = "Administrative";
    }
    // Immigration holds
    else if (
      holdName.includes("immigration") ||
      holdName.includes("visa") ||
      holdName.includes("i-20") ||
      holdName.includes("international") ||
      holdName.includes("passport")
    ) {
      type = "Immigration";
    }

    // Create a proper Hold object from the API response
    const hold: Hold = {
      id: apiHold?.id || `hold-${Date.now()}`,
      type: type,
      isActive: apiHold?.isActive,
      title: apiHold?.customHoldName,
      description: apiHold?.description,
      appliedDate: apiHold?.startDate
        ? new Date(apiHold.startDate)
        : new Date(),
      expiryDate: apiHold?.endDate ? new Date(apiHold.endDate) : undefined,
      amount: apiHold?.amount,
      statusUpdatedBy: apiHold?.statusUpdatedBy,
    };

    console.log("Mapped hold:", hold);
    return hold;
  }

  // Get students with admission (for search dropdown)
  getStudentsWithAdmission(): Observable<Student[]> {
    return this.http.get<any[]>(`/user/students/with-admission`).pipe(
      map((response) => {
        console.log("Raw API response:", response);

        if (!response || !Array.isArray(response)) {
          console.warn("API response is not an array:", response);
          return [];
        }

        // Remove duplicates based on studentId
        const uniqueStudents = new Map();
        response.forEach((student) => {
          if (!uniqueStudents.has(student.studentId)) {
            uniqueStudents.set(student.studentId, student);
          }
        });

        const uniqueArray = Array.from(uniqueStudents.values());
        console.log(
          `After removing duplicates: ${uniqueArray.length} students`,
        );

        const mappedStudents = uniqueArray
          .map((s) => this.mapToStudent(s))
          .filter((s) => s.id); // Remove any with no id

        console.log(`Mapped ${mappedStudents.length} students successfully`);
        return mappedStudents;
      }),
    );
  }

  getStudentHoldsSummary(
    page: number = 0,
    size: number = 10,
    sort: string = "studentName,asc",
    search: string = "",
  ): Observable<{ students: Student[]; totalElements: number }> {
    let params = new HttpParams()
      .set("page", page.toString())
      .set("size", size.toString())
      .set("sort", sort);

    if (search && search.trim()) {
      params = params.set("search", search.trim());
    }

    return this.http.get<any>(`/student-holds/summary`, { params }).pipe(
      map((response) => {
        console.log("Student holds summary response:", response);

        let studentsArray = [];
        let totalElements = 0;

        // Extract from the response structure you provided
        if (response && response.content) {
          studentsArray = response.content;
          totalElements = response.page?.totalElements || studentsArray.length;
        } else if (Array.isArray(response)) {
          studentsArray = response;
          totalElements = response.length;
        }

        const students = studentsArray
          .map((s: any) => this.mapToStudent(s))
          .filter((s: Student) => s.id);

        return {
          students: students,
          totalElements: totalElements,
        };
      }),
    );
  }

  // // Get holds by student ID
  // getHoldsByStudentId(studentId: string): Observable<Hold[]> {
  //   const numericId = parseInt(studentId);
  //   const url = `/student-holds/by-student/${numericId}`;

  //   console.log("Fetching holds from:", url);

  //   return this.http.get<any[]>(url).pipe(
  //     map((response) => {
  //       console.log("Raw holds by student response:", response);

  //       if (!response) {
  //         console.warn("Response is null or undefined");
  //         return [];
  //       }

  //       if (!Array.isArray(response)) {
  //         console.warn("Response is not an array:", response);
  //         return [];
  //       }

  //       console.log(`Received ${response.length} holds from API`);

  //       const mappedHolds = response
  //         .map((h: any, index: number) => {
  //           console.log(`Mapping hold ${index}:`, h);

  //           // Ensure each hold has an ID
  //           if (!h.id) {
  //             h.id = `temp-${index}-${Date.now()}`;
  //           }

  //           return this.mapToHold(h);
  //         })
  //         .filter((h: Hold) => h && h.id); // Filter out any invalid holds

  //       console.log(`Mapped ${mappedHolds.length} holds:`, mappedHolds);
  //       return mappedHolds;
  //     }),
  //   );
  // }

  // Get holds by student ID
  getHoldsByStudentId(studentId: string): Observable<Hold[]> {
    const numericId = parseInt(studentId);
    const url = `/student-holds/by-student/${numericId}`;

    console.log("Fetching holds from:", url);

    return this.http.get<any>(url).pipe(
      map((response) => {
        console.log("Raw holds by student response:", response);

        if (!response) {
          console.warn("Response is null or undefined");
          return [];
        }

        // Check if response has a content property (paginated response)
        let holdsArray = response;

        if (response.content && Array.isArray(response.content)) {
          console.log("Response is paginated, extracting content array");
          holdsArray = response.content;
        }

        if (!Array.isArray(holdsArray)) {
          console.warn("Response does not contain an array:", response);
          return [];
        }

        console.log(`Received ${holdsArray.length} holds from API`);

        const mappedHolds = holdsArray
          .map((h: any, index: number) => {
            console.log(`Mapping hold ${h.id}:`, h);

            // Ensure each hold has an ID
            if (!h.id) {
              h.id = `temp-${index}-${Date.now()}`;
            }

            return this.mapToHold(h);
          })
          .filter((h: Hold) => h && h.id); // Filter out any invalid holds

        console.log(`Mapped ${mappedHolds.length} holds:`, mappedHolds);
        return mappedHolds;
      }),
    );
  }

  // Get hold categories
  getHoldCategories(): Observable<any[]> {
    console.log("Fetching hold categories...");
    return this.http.get<any[]>(`/hold-categories/getall`).pipe(
      map((response) => {
        console.log("Raw hold categories response:", response);

        if (!response || !Array.isArray(response)) {
          console.warn("Hold categories response is not an array:", response);
          return [];
        }

        return response;
      }),
    );
  }

  // Create a new hold
  createHold(
    studentId: string,
    holdData: {
      holdCategoryId: number;
      customHoldName: string;
      description: string;
      startDate: Date;
      endDate: Date;
      isActive: boolean;
    },
  ): Observable<any> {
    const request: CreateHoldApiRequest = {
      studentId: parseInt(studentId),
      holdCategoryId: holdData.holdCategoryId,
      customHoldName: holdData.customHoldName,
      description: holdData.description,

      startDate: this.formatDate(holdData.startDate),
      endDate: this.formatDate(holdData.endDate),
      isActive: holdData.isActive,
    };

    console.log("Creating hold with request:", request);
    return this.http.post(`/student-holds/create`, request);
  }

  // Delete a hold
  removeHold(holdId: string): Observable<any> {
    const numericId = parseInt(holdId);
    console.log("Removing  hold with ID:", numericId);
    return this.http.patch(`/student-holds/update-status/${numericId}`, {
      isActive: false,
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
}
